/**
 * The recurring-template editor.
 *
 * Three contract facts shape this form rather than being hidden behind it:
 *
 * A template's code and currency are fixed at creation. The update ignores
 * them, so on an existing template they are shown and not offered for edit —
 * a field that silently discards what you typed is worse than no field.
 *
 * The lines are REPLACED whole on update. What you see here is therefore the
 * complete future template, not a patch against it.
 *
 * Amounts are signed the way the ledger signs a journal line: positive is a
 * debit. A template that does not sum to zero drafts an entry nobody can post,
 * so the balance is shown as you type and Save stays out of reach until it is
 * zero.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import {
  Button,
  Checkbox,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@erp/ui";

import { useAccounts } from "@/features/ledger/queries";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import {
  createRecurringJournal,
  updateRecurringJournal,
  type RecurringJournal,
  type RecurringJournalWrite,
} from "../period-end-api";

interface LineDraft {
  account_id: string | null;
  amount: string;
  description: string;
}

const emptyLine = (): LineDraft => ({ account_id: null, amount: "", description: "" });

interface FormState {
  code: string;
  name: string;
  description: string;
  currency: string;
  day_of_month: string;
  starts_on: string;
  ends_on: string;
  auto_reverse: boolean;
  active: boolean;
  lines: LineDraft[];
}

function initialForm(template: RecurringJournal | null): FormState {
  if (template === null) {
    return {
      code: "",
      name: "",
      description: "",
      currency: "SAR",
      day_of_month: "1",
      starts_on: "",
      ends_on: "",
      auto_reverse: false,
      active: true,
      lines: [emptyLine(), emptyLine()],
    };
  }
  return {
    code: template.code,
    name: template.name,
    description: template.description,
    currency: template.currency,
    day_of_month: String(template.day_of_month),
    starts_on: template.starts_on,
    ends_on: template.ends_on ?? "",
    auto_reverse: template.auto_reverse,
    active: template.active,
    lines: template.lines.map((line) => ({
      account_id: line.account_id,
      amount: line.amount,
      description: line.description ?? "",
    })),
  };
}

/** Sums the signed amounts without leaving the decimal domain: the strings are
 *  scaled to the widest fraction any line carries and added as integers, so a
 *  balance is never declared by a float. */
function signedTotal(lines: readonly LineDraft[]): { text: string; zero: boolean } | null {
  let scale = 0;
  for (const line of lines) {
    if (!/^-?\d+(\.\d+)?$/.test(line.amount.trim())) return null;
    const fraction = line.amount.trim().split(".")[1] ?? "";
    scale = Math.max(scale, fraction.length);
  }
  let total = 0n;
  for (const line of lines) {
    const [whole, fraction = ""] = line.amount.trim().replace("-", "").split(".");
    const digits = `${whole}${fraction.padEnd(scale, "0")}`;
    const magnitude = BigInt(digits === "" ? "0" : digits);
    total += line.amount.trim().startsWith("-") ? -magnitude : magnitude;
  }
  const negative = total < 0n;
  const digits = (negative ? -total : total).toString().padStart(scale + 1, "0");
  const text =
    scale === 0
      ? `${negative ? "-" : ""}${digits}`
      : `${negative ? "-" : ""}${digits.slice(0, digits.length - scale)}.${digits.slice(
          digits.length - scale,
        )}`;
  return { text, zero: total === 0n };
}

export interface RecurringJournalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null opens the form for a new template. */
  template: RecurringJournal | null;
  onSaved: () => void;
}

export function RecurringJournalDialog({
  open,
  onOpenChange,
  template,
  onSaved,
}: RecurringJournalDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(() => initialForm(template));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accounts = useAccounts({ is_postable: true, active: true }, open);

  useEffect(() => {
    if (open) {
      setForm(initialForm(template));
      setError(null);
    }
  }, [open, template]);

  const options = useMemo(
    () =>
      (accounts.data ?? []).map((account) => ({
        value: account.id,
        label: `${account.code} · ${account.name}`,
      })),
    [accounts.data],
  );

  const total = signedTotal(form.lines);
  const complete =
    form.name.trim() !== "" &&
    form.starts_on !== "" &&
    (template !== null || /^[a-z][a-z0-9_]{0,62}$/.test(form.code)) &&
    form.lines.length >= 2 &&
    form.lines.every((line) => line.account_id !== null && line.amount.trim() !== "");
  const balanced = total !== null && total.zero;

  const setLine = (index: number, patch: Partial<LineDraft>) =>
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line, at) => (at === index ? { ...line, ...patch } : line)),
    }));

  const save = () => {
    if (!complete || !balanced) return;
    setBusy(true);
    setError(null);
    const body: RecurringJournalWrite = {
      name: form.name.trim(),
      day_of_month: Number(form.day_of_month),
      starts_on: form.starts_on,
      // Absent and cleared are the same thing here: a PUT replaces the whole
      // template, so an empty field means the template no longer ends.
      ends_on: form.ends_on === "" ? null : form.ends_on,
      auto_reverse: form.auto_reverse,
      active: form.active,
      lines: form.lines.map((line) => ({
        account_id: line.account_id as string,
        amount: line.amount.trim(),
        ...(line.description.trim() === "" ? {} : { description: line.description.trim() }),
      })),
      ...(form.description.trim() === "" ? {} : { description: form.description.trim() }),
      // Fixed at creation, ignored by the update — so they are only sent once.
      ...(template === null ? { code: form.code, currency: form.currency } : {}),
    };
    const request =
      template === null
        ? createRecurringJournal(body, newIdempotencyKey())
        : updateRecurringJournal(template.id, body);
    void request
      .then(() => {
        onSaved();
        onOpenChange(false);
      })
      .catch(() => setError(t("finance.periodEnd.saveError", "The template could not be saved.")))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {template === null
              ? t("finance.periodEnd.newTemplate", "New recurring template")
              : t("finance.periodEnd.editTemplate", "Edit recurring template")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "finance.periodEnd.templateHint",
              "Positive is a debit. The lines must sum to zero, and saving replaces them whole.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="rj-code">{t("finance.periodEnd.code", "Code")}</Label>
            <Input
              id="rj-code"
              dir="ltr"
              className="font-mono"
              disabled={template !== null}
              value={form.code}
              onChange={(event) =>
                setForm({
                  ...form,
                  code: event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              {t("finance.periodEnd.fixedAtCreation", "Fixed once the template exists.")}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rj-currency">{t("finance.periodEnd.currency", "Currency")}</Label>
            <Input
              id="rj-currency"
              dir="ltr"
              maxLength={3}
              className="font-mono uppercase"
              disabled={template !== null}
              value={form.currency}
              onChange={(event) => setForm({ ...form, currency: event.target.value.toUpperCase() })}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="rj-name">{t("finance.periodEnd.name", "Name")}</Label>
            <Input
              id="rj-name"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="rj-description">
              {t("finance.periodEnd.description", "Description")}
            </Label>
            <Input
              id="rj-description"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rj-day">{t("finance.periodEnd.dayLabel", "Day of month")}</Label>
            <Input
              id="rj-day"
              type="number"
              min={1}
              max={31}
              dir="ltr"
              value={form.day_of_month}
              onChange={(event) => setForm({ ...form, day_of_month: event.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              {t("finance.periodEnd.dayHint", "Clamped to the last day of a shorter month.")}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rj-starts">{t("finance.periodEnd.startsOn", "Starts on")}</Label>
            <Input
              id="rj-starts"
              type="date"
              dir="ltr"
              value={form.starts_on}
              onChange={(event) => setForm({ ...form, starts_on: event.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rj-ends">{t("finance.periodEnd.endsOn", "Ends on")}</Label>
            <Input
              id="rj-ends"
              type="date"
              dir="ltr"
              value={form.ends_on}
              onChange={(event) => setForm({ ...form, ends_on: event.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              {t("finance.periodEnd.endsOnHint", "Leave empty for a template that does not end.")}
            </p>
          </div>
          <div className="flex flex-col justify-end gap-3 pb-1">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.auto_reverse}
                onCheckedChange={(checked) => setForm({ ...form, auto_reverse: checked === true })}
              />
              {t("finance.periodEnd.autoReverse", "Reverse in the following period")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.active}
                onCheckedChange={(checked) => setForm({ ...form, active: checked === true })}
              />
              {t("finance.periodEnd.active", "Active")}
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">{t("finance.periodEnd.lines", "Lines")}</h3>
            <span className="text-xs text-muted-foreground" dir="ltr">
              {t("finance.periodEnd.balance", "Balance")}: {total?.text ?? "—"}
            </span>
          </div>
          {form.lines.map((line, index) => (
            <div key={index} className="grid gap-2 sm:grid-cols-[2fr_1fr_2fr_auto]">
              <Combobox
                options={options}
                value={line.account_id}
                onChange={(value) => setLine(index, { account_id: value })}
                placeholder={t("finance.periodEnd.pickAccount", "Account…")}
                searchPlaceholder={t("actions.search", "Search")}
                emptyMessage={t("finance.periodEnd.noAccounts", "No account matches.")}
                aria-label={t("finance.periodEnd.account", "Account")}
              />
              <Input
                dir="ltr"
                inputMode="decimal"
                className="text-end font-mono"
                aria-label={t("finance.periodEnd.amount", "Amount")}
                value={line.amount}
                onChange={(event) => setLine(index, { amount: event.target.value })}
              />
              <Input
                aria-label={t("finance.periodEnd.lineDescription", "Line description")}
                value={line.description}
                onChange={(event) => setLine(index, { description: event.target.value })}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={form.lines.length <= 2}
                aria-label={t("finance.periodEnd.removeLine", "Remove line")}
                onClick={() =>
                  setForm({ ...form, lines: form.lines.filter((_, at) => at !== index) })
                }
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("finance.periodEnd.addLine", "Add line")}
          </Button>
          {total !== null && !total.zero ? (
            <p className="text-sm text-destructive">
              {t(
                "finance.periodEnd.unbalanced",
                "The lines are off by {{amount}}. A template that does not balance drafts an entry nobody can post.",
                { amount: total.text },
              )}
            </p>
          ) : null}
        </div>

        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("actions.cancel", "Cancel")}
          </Button>
          <Button type="button" disabled={!complete || !balanced || busy} onClick={save}>
            {t("actions.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
